import type { Mutation, PushOutcome, PushResult } from './outbox.js'
import type { SyncTransport } from './sync.js'
import { apiBase } from './config.js'

/**
 * The HTTP `SyncTransport` (docs/02 §8).
 *
 * `syncOnce` owns the sync algorithm; this owns nothing but the wire. Three
 * rules keep it that way, and each of them exists because the alternative
 * silently loses data:
 *
 *  1. **Every request is time-boxed.** Office wifi in a building with a captive
 *     portal accepts the TCP connection and then never answers. Without an
 *     `AbortController` on a timer, one such request wedges the sync loop for as
 *     long as the tablet is awake — no error, no retry, no sync.
 *
 *  2. **Failures THROW.** `syncOnce` catches, records the message and leaves the
 *     outbox untouched. A transport that swallowed a network error and returned
 *     an empty result would look like "the server accepted nothing", and the
 *     queue would be cleared of writes the server never saw. Losing someone's
 *     expense because the wifi blinked is not acceptable.
 *
 *  3. **Nothing retries here.** Retry policy, backoff and the
 *     rejected-is-terminal rule all live in the outbox. A retry hidden inside
 *     the transport would multiply with the one outside it and hammer the VPS.
 *
 * A malformed response is treated exactly like a failure. Quietly accepting a
 * response whose shape we do not recognise would let `reconcile` delete queued
 * mutations that were never applied — the same data loss as (2), arriving by a
 * politer route.
 */

export const DEFAULT_TIMEOUT_MS = 20_000

export type TransportFailure = 'network' | 'timeout' | 'http' | 'malformed' | 'unauthorized'

export class TransportError extends Error {
  readonly kind: TransportFailure
  readonly status: number | null

  constructor(kind: TransportFailure, message: string, status: number | null = null, options?: ErrorOptions) {
    super(message, options)
    this.name = 'TransportError'
    this.kind = kind
    this.status = status
  }

  /**
   * Whether trying again could plausibly help. A 4xx says the request itself is
   * wrong, so replaying it forever is the head-of-line blocking failure from
   * docs/02 §8 wearing a different hat. 408 and 429 are the two that do pass.
   */
  get retryable(): boolean {
    // A 401 is its own kind precisely so it cannot be retried by accident: the
    // token is wrong or revoked, and replaying the request until the heat death
    // of the universe will not make it right. The UI signs the person back in.
    if (this.kind === 'unauthorized') return false
    if (this.kind !== 'http' || this.status === null) return true
    if (this.status === 408 || this.status === 429) return true
    return this.status < 400 || this.status >= 500
  }
}

export interface HttpTransportOptions {
  /** Defaults to `apiBase()` — the single source of truth for where the server is. */
  baseUrl?: string
  /** Injected in tests; defaults to the runtime's own `fetch`. */
  fetchImpl?: typeof fetch
  timeoutMs?: number
  /** Extra headers, e.g. this device's bearer token. */
  headers?: Record<string, string>
  /**
   * The signed-in session token.
   *
   * A FUNCTION, not a string, on purpose. The transport is created once for the
   * app's lifetime — recreating it would abort whatever is in flight and restart
   * the sync loop — so it reads the current token per request instead of
   * capturing one. Signing in mid-session therefore needs no new transport, and
   * signing out stops sending the old token immediately.
   */
  token?: string | null | (() => string | null | undefined)
  /**
   * Called when the server rejects the token. `syncOnce` flattens every failure
   * to a message string, so this callback is how the UI learns that the session
   * is gone and it should ask for a password rather than retry forever.
   *
   * It is handed THE TOKEN THAT WAS REFUSED (null when the request carried
   * none), because a 401 can outlive the token it was sent with: a request
   * issued while signed out, or with the previous token, can land after a fresh
   * sign-in has already succeeded. Signing that new session out on the strength
   * of an answer about an old one is a bug the caller can only avoid if it is
   * told which token the server actually rejected.
   */
  onUnauthorized?: (rejectedToken: string | null) => void
  /** Aborts anything in flight — pass an unmount signal to hang up cleanly. */
  signal?: AbortSignal
}

const describeError = (e: unknown): string => (e instanceof Error ? e.message : String(e))
const truncate = (s: string, n = 200): string => (s.length > n ? `${s.slice(0, n)}…` : s)

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const OUTCOMES: readonly string[] = ['applied', 'rejected', 'duplicate']

/** The wire form of a queued mutation. `attempts`/`lastError` are local bookkeeping. */
export interface WireMutation {
  id: string
  kind: string
  payload: Record<string, unknown>
}

export const toWire = (m: Mutation): WireMutation => ({ id: m.id, kind: m.kind, payload: m.payload })

function parsePushResults(body: unknown, path: string): PushResult[] {
  if (!isRecord(body) || !Array.isArray(body.results)) {
    throw new TransportError('malformed', `${path} returned no results array`)
  }
  return body.results.map((r, i) => {
    if (!isRecord(r) || typeof r.mutation_id !== 'string' || typeof r.outcome !== 'string'
        || !OUTCOMES.includes(r.outcome)) {
      throw new TransportError('malformed', `${path} result ${i} is not a push result`)
    }
    // A verdict we cannot read is a verdict we must not act on: `reconcile`
    // deletes from the outbox on the strength of it.
    return {
      mutation_id: r.mutation_id,
      outcome: r.outcome as PushOutcome,
      reason: typeof r.reason === 'string' ? r.reason : null,
    }
  })
}

type PullPage = { table: string; rows: unknown[]; hasMore: boolean; nextCursor: string | null }

function parsePullPage(table: string, p: unknown, path: string): PullPage {
  if (!isRecord(p) || !Array.isArray(p.rows) || typeof p.hasMore !== 'boolean') {
    throw new TransportError('malformed', `${path} page "${table}" is not a table page`)
  }
  // "There may be more" is an EXPLICIT server signal (docs/02 §8, Landmine 1).
  // If it is missing we refuse the page rather than inferring completeness from
  // the array length, which is exactly the bug that produces wrong streaks.
  return {
    table: typeof p.table === 'string' ? p.table : table,
    rows: p.rows,
    hasMore: p.hasMore,
    nextCursor: typeof p.nextCursor === 'string' ? p.nextCursor : null,
  }
}

export function httpTransport(opts: HttpTransportOptions = {}): SyncTransport {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const base = (): string => (opts.baseUrl ?? apiBase()).replace(/\/+$/, '')

  /**
   * One request, headers AND body inside a single deadline.
   *
   * The timer deliberately spans the body read as well as the fetch. Headers
   * arriving is not an answer: a captive portal that returns `200` and then
   * stalls the stream wedges the loop exactly as a silent socket does, and a
   * deadline that stops at the headers cannot see the difference.
   */
  async function json<T>(path: string, method: 'GET' | 'POST', body?: unknown): Promise<T> {
    const doFetch = opts.fetchImpl ?? (globalThis as { fetch?: typeof fetch }).fetch
    if (!doFetch) throw new TransportError('network', 'no fetch in this runtime')

    const ctl = new AbortController()
    const onOuterAbort = (): void => ctl.abort()
    if (opts.signal) {
      if (opts.signal.aborted) ctl.abort()
      else opts.signal.addEventListener('abort', onOuterAbort, { once: true })
    }

    let timedOut = false
    const timer = setTimeout(() => { timedOut = true; ctl.abort() }, timeoutMs)

    // Explicit `headers` win over the token, so a caller can always say exactly
    // what it wants on the wire. Their names are lower-cased first: HTTP header
    // names are case-insensitive, so an `Authorization` that stayed capitalised
    // would sit BESIDE our `authorization` and be sent as two values rather than
    // replacing it.
    const explicit: Record<string, string> = {}
    for (const [k, v] of Object.entries(opts.headers ?? {})) explicit[k.toLowerCase()] = v

    const raw = typeof opts.token === 'function' ? opts.token() : opts.token
    const token = typeof raw === 'string' && raw !== '' ? raw : null
    const headers: Record<string, string> = {
      accept: 'application/json',
      ...(token !== null ? { authorization: `Bearer ${token}` } : {}),
      ...explicit,
    }
    if (body !== undefined && headers['content-type'] === undefined) {
      headers['content-type'] = 'application/json'
    }

    // Which half we are in, so an abort mid-body is reported as the timeout it
    // is rather than as unreadable JSON.
    let reading = false
    let status: number | null = null

    try {
      const res = await doFetch(`${base()}${path}`, {
        method,
        headers,
        signal: ctl.signal,
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      })
      status = res.status
      reading = true
      if (!res.ok) {
        const detail = await res.text().catch(() => '')
        if (res.status === 401) {
          // Distinct, and non-retryable: the session is gone, not the network.
          // The token that was refused goes with it — see `onUnauthorized`.
          opts.onUnauthorized?.(token)
          throw new TransportError('unauthorized', `${path} → 401 ${truncate(detail)}`.trim(), 401)
        }
        throw new TransportError('http', `${path} → ${res.status} ${truncate(detail)}`.trim(), res.status)
      }
      return (await res.json()) as T
    } catch (e) {
      if (e instanceof TransportError) throw e
      if (timedOut) {
        throw new TransportError('timeout', `${path} timed out after ${timeoutMs}ms`, status, { cause: e })
      }
      if (opts.signal?.aborted) {
        throw new TransportError('network', `${path} aborted`, status, { cause: e })
      }
      if (reading) {
        throw new TransportError('malformed', `${path} returned invalid JSON`, status, { cause: e })
      }
      throw new TransportError('network', `${path} failed: ${describeError(e)}`, null, { cause: e })
    } finally {
      clearTimeout(timer)
      opts.signal?.removeEventListener('abort', onOuterAbort)
    }
  }

  return {
    async push(deviceId, mutations) {
      const path = '/sync/push'
      const body = await json<unknown>(path, 'POST', { deviceId, mutations: mutations.map(toWire) })
      return { results: parsePushResults(body, path) }
    },

    async pull(cursors) {
      // Only send cursors that actually point somewhere; a first sync asks for
      // everything and an empty object is noise in the log.
      const meaningful = Object.fromEntries(
        Object.entries(cursors).filter(([, v]) => typeof v === 'string' && v.length > 0),
      )
      const q = Object.keys(meaningful).length
        ? `?cursors=${encodeURIComponent(JSON.stringify(meaningful))}`
        : ''
      const path = `/sync/pull${q}`
      const body = await json<unknown>(path, 'GET')
      if (!isRecord(body) || !isRecord(body.tables) || typeof body.incomplete !== 'boolean') {
        throw new TransportError('malformed', `${path} returned no pull result`)
      }
      const tables: Record<string, PullPage> = {}
      for (const [table, page] of Object.entries(body.tables)) {
        tables[table] = parsePullPage(table, page, path)
      }
      return {
        tables,
        incomplete: body.incomplete,
        serverTime: typeof body.serverTime === 'string' ? body.serverTime : '',
      }
    },

    async time() {
      const path = '/time'
      const body = await json<unknown>(path, 'GET')
      // The EPOCH, never a wall-clock string (docs/02 §4): a string would be
      // re-parsed through the device's own stale tzdata and land an hour out.
      const epochMs = isRecord(body) ? body.epochMs : undefined
      if (typeof epochMs !== 'number' || !Number.isFinite(epochMs)) {
        throw new TransportError('malformed', `${path} returned no numeric epochMs`)
      }
      return { epochMs }
    },
  }
}
